import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "component/ui/Dialog";
import { Button } from "component/ui/Button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "component/ui/Dropdown";
import { ChevronDown } from "lucide-react";
import Loader from "component/loader";
import {
    WalletStepIndicator,
    AccountOpeningFeeBanner,
} from "./OpenWalletInfoModal";
import useOpeningFees from "hooks/useOpeningFees";
import NGNFlagImg from "asset/images/Currency.svg";
import USFlagImg from "asset/images/US.png";
import GBPFlagImg from "asset/images/GB.svg";
import EURFlagImg from "asset/images/EU.svg";
import KESIcon from "asset/icons/KES.svg";
import GHSIcon from "asset/icons/GH.svg";
import USDCIcon from "asset/icons/USDC.svg";
import USDTIcon from "asset/icons/USDT.svg";
import currencyPairCheck from "utilities/currencyPairCheck";
import { fetchCryptoRate, fetchFxRate } from "api/fetchers";
import { useQuery } from "react-query";
import { currencyToDisplay } from "../flex/utils";
import { toast } from "react-toastify";
import { cn } from "lib/utils";

const CURRENCY_FLAG_MAP = {
    NGN: NGNFlagImg,
    USD: USFlagImg,
    GBP: GBPFlagImg,
    EUR: EURFlagImg,
    KES: KESIcon,
    GHS: GHSIcon,
    USDC: USDCIcon,
    USDT: USDTIcon,
};

const OpenWalletPaymentModal = ({
    isOpen,
    currencyCode = "KES",
    onClose,
    onBack,
    onSubmit,
    isLoading = false,
    step = 2,
    totalSteps = 2,
    customDeductText,
}) => {
  const { wallets: storeWallets, business } = useSelector((state) => state);
  const normalizedTargetCode = String(currencyCode || "").toUpperCase();
  const businessId = business?._id;

  const availablePaymentAccounts = useMemo(() => {
    const list = Array.isArray(storeWallets) ? storeWallets : [];
    const valid = list.map((w) => {
      const code = String(w?.currency || "").toUpperCase();
      const providers = Array.isArray(w?.providers) ? w.providers : [];
      const primaryProvider =
        providers.find((p) => p?.accountNumber || p?.bankName) || providers[0] || {};
      const bankName =
        primaryProvider?.bankName ||
        primaryProvider?.bank ||
        (code === "NGN" ? "Providus bank" : "Tradevu Wallet");
      const balance = Number(w?.balance || 0);
      return {
        currency: code,
        bankName: `${code} - ${bankName}`,
        balance: balance,
        symbol: currencyPairCheck(code),
        flag: CURRENCY_FLAG_MAP[code] || NGNFlagImg,
        raw: w,
      };
    });

    if (valid.length === 0) {
      return [
        {
          currency: "NGN",
          bankName: "NGN - Providus bank",
          balance: 10080000.5,
          symbol: "₦",
          flag: NGNFlagImg,
        },
      ];
    }
    return valid;
  }, [storeWallets]);
  const [selectedAccount, setSelectedAccount] = useState(() => {
    const ngnAcc = availablePaymentAccounts.find((a) => a.currency === "NGN");
    return ngnAcc || availablePaymentAccounts[0] || null;
  });

  const isCrypto = ["USDT", "USDC"].includes(normalizedTargetCode);

  const { openingFees } = useOpeningFees();

  const feeItem = Array.isArray(openingFees)
    ? openingFees.find(
      (item) =>
        String(item?.currency || item?.code || item?.currencyCode || "").toUpperCase() ===
        normalizedTargetCode
    )
    : null;


  const { data, isLoading: isLoadingTrxRate, isError } = useQuery({
    queryFn: () =>
      isCrypto ? fetchCryptoRate({ cryptoCurrency: normalizedTargetCode, fiatCurrency: selectedAccount?.currency, businessId }) : fetchFxRate({
        fromCurrency: feeItem?.currency,
        toCurrency: selectedAccount?.currency,
        amount: feeItem?.amount,
      }),
    queryKey: ["fxRate", currencyCode, selectedAccount?.currency],
    enabled: !!selectedAccount?.currency && !!feeItem?.currency,
    onSettled: (res, error) => {
      if (error?.message) {
        if (error?.message.includes('Service not available for the specified currency pair')) {
          return toast.error('Swaps between this currency pair is currently not available.\nPlease try another wallet');
        }
        return toast.error(error?.message);
      }
    },
  });

  const rate = isCrypto ? data : data?.roundedRate;
  console.log({ data, rate, selectedAccount, isCrypto, feeItem, normalizedTargetCode, businessId })


  const dynamicDeductText = `${currencyToDisplay(selectedAccount?.currency, feeItem?.amount * rate)} ~ ${currencyToDisplay(feeItem?.currency, feeItem?.amount)}`;

  const handleFormSubmit = () => {
    if (onSubmit) {
      onSubmit(selectedAccount);
    }
  };

  const feeAmountInChargedCurrency = (feeItem?.amount || 0) * (rate || 1);
  const isInsufficientBalance = Boolean(
    !isLoadingTrxRate &&
    feeItem?.amount &&
    Number(selectedAccount?.balance || 0) < feeAmountInChargedCurrency
  );

  const isDisabled =
    isLoadingTrxRate ||
    isError ||
    !selectedAccount ||
    isLoading ||
    isInsufficientBalance;

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose?.()}>
            <DialogContent
                showCloseButton={true}
          className="max-w-[480px] sm:max-w-[560px] p-4 rounded-[16px] bg-white border-0 shadow-2xl overflow-hidden"
            >
                {/* Header Title */}
                <DialogTitle className="text-[20px] sm:text-[22px] font-bold text-[#101828] tracking-tight">
                    Open a new wallet
                </DialogTitle>
                <DialogDescription className="sr-only">
                    Select an account to be charged for the opening fee
                </DialogDescription>

                <div className="">
            <label className="block text-[13.5px] sm:text-[14px] font-medium text-[#494E6E] mb-2.5">
                        Which account should be charged?
                    </label>

                    {/* Account Selector Dropdown */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                <div className="w-full border border-[#D0D5DD] hover:border-[#6F00FF] transition-colors rounded-[12px] p-3.5 flex items-center justify-between cursor-pointer bg-white disabled:bg-[#EBEDF9]">
                                <div className="flex items-center gap-3">
                                    <img
                                        src={selectedAccount?.flag || NGNFlagImg}
                                        alt={selectedAccount?.currency || "currency"}
                                        className="w-7 h-7 rounded-full object-cover shrink-0"
                                    />
                    <span className="text-[14px] sm:text-base font-medium text-[#101828]">
                                        {selectedAccount?.bankName || "Select payment account"}
                                    </span>
                                </div>
                                <ChevronDown className="w-5 h-5 text-[#667085]" />
                            </div>
                        </DropdownMenuTrigger>

                        <DropdownMenuContent
                            align="start"
                className="w-[calc(100vw-48px)] sm:w-[510px] max-h-60 overflow-y-auto bg-white rounded-xl shadow-xl border border-gray-100 p-1.5 z-[1050]"
                        >
                            {availablePaymentAccounts.map((acc) => (
                                <DropdownMenuItem
                                    key={acc.currency}
                                    onClick={() => setSelectedAccount(acc)}
                                    className="flex items-center justify-between p-3 rounded-lg hover:bg-[#F9FAFB] cursor-pointer"
                                >
                                    <div className="flex items-center gap-3">
                                        <img
                                            src={acc.flag}
                                            alt={acc.currency}
                                            className="w-7 h-7 rounded-full object-cover"
                                        />
                                        <div>
                                            <p className="text-sm font-medium text-[#101828]">{acc.bankName}</p>
                                            <p className="text-xs text-[#667085]">
                                                Balance: {currencyToDisplay(acc?.currency, acc?.balance || 0)}
                                            </p>
                                        </div>
                                    </div>
                                    {selectedAccount?.currency === acc.currency && (
                                        <span className="w-2 h-2 rounded-full bg-[#6F00FF]" />
                                    )}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>

                    {/* Balance Info */}
            <p className="text-[12.5px] sm:text-[13px] text-[#667085] mt-2">
              Balance: <span className={cn("font-medium", isInsufficientBalance && 'text-danger-20')}>{currencyToDisplay(selectedAccount?.currency, selectedAccount?.balance || 0)}</span>
            </p> 
                </div>

                {/* Amount to Deduct Banner */}

                    <AccountOpeningFeeBanner
                        showIcon={false}
            className="justify-center text-xs text-center"
                    >
            <span className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap">
              <span>Amount to deduct:</span>
              {isLoadingTrxRate ? (
                <span className="inline-flex items-center">
                  <Loader color="#6F00FF" />
                </span>
              ) : (
                  <span className="">{dynamicDeductText}</span>
              )}
            </span>
          </AccountOpeningFeeBanner>

                {/* Footer with Stepper & Action buttons */}
                <div className="flex items-center justify-between">
                    <WalletStepIndicator currentStep={step} totalSteps={totalSteps} />

                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            onClick={onBack}
                            disabled={isLoading}
                            className="text-[#344054] hover:text-[#101828] hover:bg-transparent font-medium text-[14.5px] px-3 py-2"
                        >
                            Back
                        </Button>
                        <Button
                            onClick={handleFormSubmit}
                            disabled={isDisabled}
                            className="bg-[#6F00FF] hover:bg-[#5E00DB] text-white font-semibold text-[14.5px] px-6 py-2.5 shadow-none transition-all"
                        >
                            {isLoading ? <Loader /> : "Continue"}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default OpenWalletPaymentModal;
